import React, { useMemo } from 'react';
import classNames from 'classnames';
import { TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { TextFieldProps } from '@mui/material/TextField';
import { useFeatureFlag } from 'modules/common/hooks';

// Styles overrides are required
// Dependant on https://github.com/mui-org/material-ui/issues/21409

const useStyles = makeStyles({
  hiddenLabel: {
    '&>div': {
      paddingTop: '0px!important'
    }
  },
  hiddenLabelInput: {
    '&>input.MuiFilledInput-inputHiddenLabel.MuiFilledInput-inputMarginDense': {
      padding: '10px 12px 11px !important'
    }
  }
});

export type AutocompleteInputProps = TextFieldProps & {
  hiddenLabel?: boolean;
  showError?: boolean;
  errorMessage: React.ReactNode | string;
  alwaysShowHelperText: boolean;
  label?: string;
};

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  hiddenLabel = false,
  variant,
  showError = false,
  errorMessage,
  placeholder,
  helperText,
  alwaysShowHelperText,
  label,
  ...textFieldInputProps
}: AutocompleteInputProps) => {
  const classes = useStyles();

  const helperTextMessage = useMemo(
    () => (showError && errorMessage) || (alwaysShowHelperText && helperText),
    [showError, errorMessage, alwaysShowHelperText, helperText]
  );
  const { InputProps, className, inputProps, ...rest } = textFieldInputProps;
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  const inputPropValue = useMemo(
    () => ({
      'aria-label': isTiwScreenReaderEnabled ? label : undefined,
      ...inputProps
    }),
    [inputProps, isTiwScreenReaderEnabled, label]
  );

  const autocompleteInputProps = useMemo(() => {
    const autocompleteInputProps = {
      ...InputProps,
      className: classNames(InputProps?.className, {
        [classes.hiddenLabelInput]: hiddenLabel
      }),
      inputProps: inputPropValue
    };
    return autocompleteInputProps;
  }, [InputProps, classes.hiddenLabelInput, hiddenLabel, inputPropValue]);

  if (hiddenLabel) {
    return (
      <TextField
        error={showError}
        helperText={helperTextMessage}
        className={classNames(className, {
          [classes.hiddenLabel]: hiddenLabel
        })}
        placeholder={placeholder}
        variant={variant}
        InputProps={autocompleteInputProps}
        hiddenLabel={hiddenLabel}
        {...rest}
      />
    );
  }

  return (
    <TextField
      error={showError}
      helperText={helperTextMessage}
      variant={variant}
      {...textFieldInputProps}
      placeholder={placeholder}
    />
  );
};

export default AutocompleteInput;
